import { InteractiveContainer } from "@jugl-web/ui-components/cross-platform/InteractiveContainer";
import { Text } from "@jugl-web/ui-components/cross-platform/Text";
import { cx, useTranslations } from "@jugl-web/utils";
import React, { useEffect, useMemo, useState } from "react";
import mime from "mime";
import { ReactComponent as PlayIcon } from "./assets/play.svg";

export const FileTile: React.FC<{
  file: File | string;
  isSelected: boolean;
  mimeType: string;
  hasOverlay?: boolean;
  overlayText?: string;
  preview?: string;
  onClick: () => void;
}> = ({
  isSelected,
  file,
  preview,
  mimeType,
  hasOverlay,
  overlayText,
  onClick,
}) => {
  const { t } = useTranslations();
  const fileExtension = mime.getExtension(mimeType);
  const [isPreviewLoaded, setIsPreviewLoaded] = useState(false);
  const isImage = mimeType.startsWith("image/");
  const isVideo = mimeType.startsWith("video/");

  const previewSrc = useMemo(() => {
    if (preview) return preview;

    if (isImage || isVideo) {
      return typeof file === "string" ? file : URL.createObjectURL(file);
    }

    return undefined;
  }, [preview, file, isImage, isVideo]);

  useEffect(() => {
    return () => {
      if (previewSrc && typeof file !== "string") {
        URL.revokeObjectURL(previewSrc);
      }
    };
  }, [previewSrc, file]);

  return (
    <InteractiveContainer
      className={cx(
        "border-grey-400 relative !box-content h-[46px] w-[46px] shrink-0 overflow-hidden rounded-[10px] border border-solid bg-white transition-colors",
        isSelected && "border-primary border-[3px]"
      )}
      onClick={onClick}
    >
      {!isPreviewLoaded && (
        <Text
          variant="body3"
          className="text-primary absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transform font-medium"
        >
          {fileExtension ||
            t({
              id: "common.file",
              defaultMessage: "file",
            })}
        </Text>
      )}
      {previewSrc && isImage && (
        <img
          src={previewSrc}
          alt={typeof file === "string" ? "" : file.name}
          className="h-full w-full object-cover object-center"
          hidden={!isPreviewLoaded}
          onLoad={() => setIsPreviewLoaded(true)}
        />
      )}
      {previewSrc && isVideo && (
        <video
          src={`${previewSrc}#t=0.001`}
          className="h-full w-full object-cover object-center"
          muted
          hidden={!isPreviewLoaded}
          preload="metadata"
          onLoadedMetadata={() => setIsPreviewLoaded(true)}
        />
      )}
      {isPreviewLoaded && isVideo && (
        <PlayIcon className="absolute top-1/2 left-1/2 h-[22px] w-[22px] -translate-x-1/2 -translate-y-1/2" />
      )}
      {hasOverlay && (
        <div className="absolute top-0 left-0 flex h-full w-full items-center justify-center bg-black/50">
          <span className="font-secondary text-sm font-semibold leading-[150%] tracking-[0.14px] text-white">
            {overlayText}
          </span>
        </div>
      )}
    </InteractiveContainer>
  );
};
